import { Suspense, lazy } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';

import { Loader } from '@plot/ui';

import { TrackAreaReportViewed } from '@/lib/analytics/components';
import { AreaReportTrackerProvider } from '@/lib/analytics/contexts/area-report';
import { msalConfig } from '@/lib/msal';
import ActivityReportsGuard from '@/pages/activity-reports/ActivityReportsGuard';
import ActivityReportNewBuildsUnitTable from '@/pages/activity-reports/activity-report/ActivityReportNewBuildsUnitsTable';
import ActivityReportPriceChanges from '@/pages/activity-reports/activity-report/ActivityReportPriceChanges';
import ActivityReportUnitCategory from '@/pages/activity-reports/activity-report/ActivityReportUnitCategory';
import NotFoundPage from '@/pages/not-found/NotFound';

import {
  AppLayout,
  AuthLayout,
  PageLayout,
  WorkspaceLayout,
} from '@/components/layouts';
import { CadastralPlotLayout } from '@/components/layouts/workspace/cadastral-plot';

//pages
const MapExplorer = lazy(() => import('@/pages/map-explorer'));
const Dashboard = lazy(() => import('@/pages/dashboard'));
const ActivityReports = lazy(() => import('@/pages/activity-reports'));

const ActivityReport = lazy(
  () => import('@/pages/activity-reports/activity-report')
);
const CreateActivityReport = lazy(
  () => import('@/pages/activity-reports/activity-report/create')
);
const EditActivityReport = lazy(
  () => import('@/pages/activity-reports/activity-report/edit')
);
const CreatedActivityReport = lazy(
  () => import('@/pages/activity-reports/activity-report/created')
);

const AreaReport = lazy(() => import('../pages/area-report'));
// const Wizard = lazy(() => import('../pages/workspace/_wizard'));
const Settings = lazy(() => import('../pages/workspace/settings'));
const Members = lazy(() => import('../pages/workspace/members'));
// const Subscription = lazy(
//   () => import('../pages/workspace/_wizard/subscription')
// );
// const Invoices = lazy(() => import('../pages/workspace/_invoices'));
const Profile = lazy(() => import('../pages/account/profile'));
const Billing = lazy(() => import('../pages/account/billing'));
const Signin = lazy(() => import('../pages/auth/signin'));
const Signup = lazy(() => import('../pages/auth/signup'));
const Verify = lazy(() => import('../pages/auth/signup/verify'));
const ForgotPassword = lazy(() => import('../pages/auth/forgot-password'));
const ResetPassword = lazy(() => import('../pages/auth/reset-password'));
const NotSupported = lazy(() => import('../pages/auth/not-supported'));

const msalInstance = new PublicClientApplication(msalConfig);

const activityReportsGuard = (element: JSX.Element) => (
  <ActivityReportsGuard>{element}</ActivityReportsGuard>
);

const authRoutes = [
  {
    element: <AuthLayout />,
    children: [
      {
        path: 'signin',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loader backdrop cover />}>
                <GoogleOAuthProvider
                  clientId={import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID}
                >
                  <MsalProvider instance={msalInstance}>
                    <Signin />
                  </MsalProvider>
                </GoogleOAuthProvider>
              </Suspense>
            ),
          },
          {
            path: 'forgot-password',
            element: (
              <Suspense fallback={<Loader backdrop cover />}>
                <ForgotPassword />
              </Suspense>
            ),
          },
          {
            path: 'reset-password/:user/:token',
            element: (
              <Suspense fallback={<Loader backdrop cover />}>
                <ResetPassword />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'signup',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loader backdrop cover />}>
                <GoogleOAuthProvider
                  clientId={import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID}
                >
                  <MsalProvider instance={msalInstance}>
                    <Signup />
                  </MsalProvider>
                </GoogleOAuthProvider>
              </Suspense>
            ),
          },
          {
            path: 'verify',
            element: (
              <Suspense fallback={<Loader backdrop cover />}>
                <Verify />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
];

const workspaceSettingsRoutes = [
  {
    element: <PageLayout />,
    children: [
      {
        path: '/workspaces/:workspace',
        element: <Navigate to="dashboard" replace />, // Redirect to dashboard when at workspace root
      },
      {
        path: '/profile',
        element: (
          <Suspense fallback={<Loader backdrop cover />}>
            <Profile />
          </Suspense>
        ),
      },
      {
        path: '/billing',
        element: (
          <Suspense fallback={<Loader backdrop cover />}>
            <Billing />
          </Suspense>
        ),
      },
      {
        path: '/workspaces/:workspace/settings',
        element: (
          <Suspense fallback={<Loader backdrop cover />}>
            <Settings />
          </Suspense>
        ),
      },
      {
        path: '/workspaces/:workspace/members',
        element: (
          <Suspense fallback={<Loader backdrop cover />}>
            <Members />
          </Suspense>
        ),
      },
    ],
  },
  //
  // {
  //   children: [
  //     {
  //       path: '/workspaces/:workspace/settings',
  //       element: (
  //         <Suspense fallback={<Loader backdrop cover />}>
  //           <Settings />
  //         </Suspense>
  //       ),
  //     },
  //     {
  //       path: '/workspaces/:workspace/members',
  //       element: (
  //         <Suspense fallback={<Loader backdrop cover />}>
  //           <Members />
  //         </Suspense>
  //       ),
  //     },
  // {
  //   path: 'subscription',
  //   element: (
  //     <Suspense fallback={<Loader backdrop cover />}>
  //       <Subscription showHeader />
  //     </Suspense>
  //   ),
  // },
  // {
  //   path: 'invoices',
  //   element: (
  //     <Suspense fallback={<Loader backdrop cover />}>
  //       <Invoices />
  //     </Suspense>
  //   ),
  // },
  //   ],
  // },
];

const otherRoutes = [
  {
    children: [
      {
        path: '/not-supported',
        element: <NotSupported />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];

const mapExplorerRoutes = [
  {
    path: 'workspaces/:workspace/map-explorer',
    element: (
      <Suspense fallback={<Loader backdrop cover />}>
        <MapExplorer />
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: 'cadastral-plots',
        element: <CadastralPlotLayout />,
        children: [
          {
            path: ':cadastralPlotId',
          },
          {
            path: ':cadastralPlotId/*',
          },
        ],
      },
      {
        path: 'cadastral-units',
        element: <CadastralPlotLayout />,
        children: [
          {
            path: ':cadastralUnitId',
          },
          {
            path: ':cadastralUnitId/*',
          },
        ],
      },
    ],
  },
];

const activityReportsRoutes = [
  {
    path: 'workspaces/:workspace/activity-reports',
    element: (
      <Suspense fallback={<Loader backdrop cover />}>
        <ActivityReports />
      </Suspense>
    ),
  },
  {
    path: 'workspaces/:workspace/activity-reports/:activityReportId',
    element: activityReportsGuard(
      <Suspense fallback={<Loader backdrop cover />}>
        <ActivityReport />
      </Suspense>
    ),
    children: [
      {
        path: 'price-changes',
        element: activityReportsGuard(
          <Suspense fallback={<Loader backdrop cover />}>
            <ActivityReportPriceChanges />
          </Suspense>
        ),
      },
      {
        path: 'units-category',
        element: activityReportsGuard(
          <Suspense fallback={<Loader backdrop cover />}>
            <ActivityReportUnitCategory />
          </Suspense>
        ),
      },
      {
        path: 'newbuilds-units-table',
        element: activityReportsGuard(
          <Suspense fallback={<Loader backdrop cover />}>
            <ActivityReportNewBuildsUnitTable />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'workspaces/:workspace/activity-reports/create',
    element: activityReportsGuard(
      <Suspense fallback={<Loader backdrop cover />}>
        <CreateActivityReport />
      </Suspense>
    ),
  },
  {
    path: 'workspaces/:workspace/activity-reports/:activityReportId/edit',
    element: activityReportsGuard(
      <Suspense fallback={<Loader backdrop cover />}>
        <EditActivityReport />
      </Suspense>
    ),
  },
  {
    path: 'workspaces/:workspace/activity-reports/:activityReportId/created',
    element: activityReportsGuard(
      <Suspense fallback={<Loader backdrop cover />}>
        <CreatedActivityReport />
      </Suspense>
    ),
  },
];

const workspaceRoutes = [
  {
    element: <WorkspaceLayout />,
    children: [
      {
        children: [
          {
            index: true,
            element: null,
          },
          ...workspaceSettingsRoutes,
          ...mapExplorerRoutes,
          ...activityReportsRoutes,
          {
            element: <PageLayout />,
            path: '/workspaces/:workspace/area-reports',
            children: [
              {
                index: true,
                path: ':reportId?',
                element: (
                  <AreaReportTrackerProvider>
                    <TrackAreaReportViewed>
                      <Suspense fallback={<Loader backdrop cover />}>
                        {/* <AreaReport /> */}
                      </Suspense>
                    </TrackAreaReportViewed>
                  </AreaReportTrackerProvider>
                ),
              },
            ],
          },
          // Adding new routes
          {
            path: 'workspaces/:workspace/dashboard',
            element: (
              <Suspense fallback={<Loader backdrop cover />}>
                <Dashboard />
              </Suspense>
            ),
          },

          {
            path: '/activity-reports/create',
            element: <Dashboard />,
          },
          {
            path: '/activity-reports/created',
            element: <Dashboard />,
          },
          {
            path: '/activity-reports/:id',
            element: <Dashboard />,
          },
          {
            path: '/activity-reports/:id/edit',
            element: <Dashboard />,
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    children: [...workspaceRoutes, ...authRoutes, ...otherRoutes],
  },
]);

export default router;
